import React, { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2, RotateCcw, FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { DEFAULT_INFO_SECTIONS, newInfoSection } from "@/pages/cotizador/shared";

// Editor de la sección "Information & Documents" del visor del cliente (se muestra al final).
export function CotInfoDocuments({ sections, show, onChange, onToggle }) {
  const [open, setOpen] = useState(false);
  const usingDefaults = !sections || sections.length === 0;
  const list = usingDefaults ? DEFAULT_INFO_SECTIONS.map((s) => newInfoSection(s)) : sections;

  const persist = (next) => onChange(next);
  const update = (id, patch) => persist(list.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const add = () => persist([...list, newInfoSection({ title: "Nueva sección", body: "" })]);
  const remove = (id) => persist(list.filter((s) => s.id !== id));
  const reset = () => persist(DEFAULT_INFO_SECTIONS.map((s) => newInfoSection(s)));

  return (
    <Card data-testid="cot-info-documents">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }}
        className="flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-3 text-left"
        data-testid="cot-info-toggle"
      >
        <span className="flex items-center gap-2 text-base font-semibold">
          <FileText className="h-4 w-4 text-primary" /> Information &amp; Documents
          <span className="text-xs font-normal text-muted-foreground">· se muestra al final del itinerario</span>
        </span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground" onClick={(e) => e.stopPropagation()}>
            <Switch checked={show !== false} onCheckedChange={onToggle} data-testid="cot-info-show" />
            {show !== false ? "Visible" : "Oculta"}
          </span>
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </span>
      </div>

      {open && (
        <CardContent className="space-y-3 border-t pt-4">
          {usingDefaults && (
            <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground" data-testid="cot-info-defaults-hint">
              Mostrando el texto estándar de Espíritu Travel. Edítalo libremente; tus cambios se guardarán con la cotización.
            </p>
          )}
          {list.map((s, i) => (
            <div key={s.id} className="rounded-lg border bg-muted/30 p-3" data-testid={`cot-info-section-${i}`}>
              <div className="mb-2 flex items-center gap-2">
                <Input
                  value={s.title || ""} onChange={(e) => update(s.id, { title: e.target.value })}
                  placeholder="Título de la sección" className="h-8 flex-1 text-sm font-medium" data-testid={`cot-info-title-${i}`}
                />
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(s.id)} aria-label="Eliminar sección" data-testid={`cot-info-remove-${i}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Textarea
                rows={4} value={s.body || ""} onChange={(e) => update(s.id, { body: e.target.value })}
                placeholder="Contenido (una línea por punto; usa • o - para listas)" className="text-sm" data-testid={`cot-info-body-${i}`}
              />
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={add} data-testid="cot-info-add"><Plus className="mr-1.5 h-4 w-4" />Añadir sección</Button>
            <Button size="sm" variant="ghost" onClick={reset} data-testid="cot-info-reset"><RotateCcw className="mr-1.5 h-4 w-4" />Restablecer estándar</Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
